'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Image,
  Container,
  Center,
  IconButton,
} from '@chakra-ui/react';
import { HLLogo } from '../ui/svgs/AppDownload/HLLogo';
import { toaster } from '../ui/toaster';
import { ApkDownloadResponse } from '@/lib/services/account.service';
import { useTranslations } from 'next-intl';

interface DownloadPageContentProps {
  apkDownloadData: ApkDownloadResponse | null;
}

export default function DownloadPageContent({ apkDownloadData }: DownloadPageContentProps) {
  const [inviteCode, setInviteCode] = useState<string>('');
  const t = useTranslations('DownloadPage');
  const isAndroid = /Android/.test(navigator.userAgent);
  
  useEffect(() => {
    // Set page title
    document.title = t('title');

    // Extract invite code from URL search params
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('inviteCode');
    if (code) {
      setInviteCode(code);
    }
  }, []);

  const handleCopyInviteCode = async () => {
    try {
      await navigator.clipboard.writeText(inviteCode);
      toaster.success({
        title: t('inviteCodeCopied'),
      });
    } catch (err) {
      toaster.error({
        title: t('failedToCopyInviteCode'),
      });
    }
  };

  const handleDownloadApp = () => {
    // Logic for app download - detect platform and redirect to appropriate store
    const userAgent = navigator.userAgent;

    if (/iPad|iPhone|iPod/.test(userAgent)) {
      // iOS - redirect to App Store
      window.open(
        'https://apps.apple.com/us/app/wildmeta/id6747332698',
        '_blank',
      );
    } else if (/Android/.test(userAgent)) {
      // Android - redirect to Google Play Store
      window.open(
        'https://play.google.com/store/apps/details?id=io.wildmeta.app',
        '_blank',
      );
    } else {
      // Desktop - show both options or default to iOS App Store
      const isMobile =
        /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent,
        );
      if (isMobile) {
        // If mobile device but not clearly iOS/Android, default to iOS
        // window.open('https://apps.apple.com/us/app/wildmeta/id6747332698', '_blank')
      } else {
        // Desktop - show options or alert
        const choice = confirm(
          'Download Wildmeta App:\n\nOK for iOS (App Store)\nCancel for Android (Google Play)',
        );
        if (choice) {
          window.open(
            'https://apps.apple.com/us/app/wildmeta/id6747332698',
            '_blank',
          );
        } else {
          window.open(
            'https://play.google.com/store/apps/details?id=io.wildmeta.app',
            '_blank',
          );
        }
      }
    }
  };

  const handleDownloadApk = () => {
    if (apkDownloadData?.url) {
      window.open(apkDownloadData.url, '_blank');
    } else {
      toaster.error({
        title: t('apkNotAvailable'),
      });
    }
  };

  return (
    <Center
      minH="100vh"
      w="full"
      bg="white"
      overflowX="hidden"
      overflowY="auto"
    >
      <Container maxW="sm" centerContent minH="100vh" px={10} py={12}>
        <VStack gap={{ base: 24, md: 32 }} align="center" w="full">
          {/* Header Section */}
          <VStack gap={4} align="center">
            {/* Logo */}
            <Box
              w="120px"
              h="120px"
              bg="black"
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="33.6px"
              boxShadow="lg"
            >
              <Image
                src="/images/logo.png"
                alt="Wildmeta Logo"
                w="120px"
                h="120px"
                objectFit="contain"
              />
            </Box>

            {/* Navigation */}
            <HStack gap={6} justify="center">
              <Text
                color="black"
                fontWeight="black"
                fontSize="28px"
                lineHeight="tight"
                letterSpacing="tight"
              >
                {t('discover')}
              </Text>
              <Text
                color="black"
                fontWeight="black"
                fontSize="28px"
                lineHeight="tight"
                letterSpacing="tight"
              >
                {t('learn')}
              </Text>
              <Text
                color="black"
                fontWeight="black"
                fontSize="28px"
                lineHeight="tight"
                letterSpacing="tight"
              >
                {t('trade')}
              </Text>
            </HStack>
          </VStack>

          {/* Action Buttons */}
          <VStack gap={6} w="full">
            {/* Primary Button - Download App */}
            <Button
              onClick={handleDownloadApp}
              w="full"
              bg="gray.900"
              color="white"
              borderRadius="full"
              h={12}
              fontWeight="medium"
              fontSize="sm"
              _hover={{ bg: 'gray.800', boxShadow: 'lg' }}
              _active={{ bg: 'gray.950' }}
              transition="all 0.2s"
              boxShadow="md"
            >
              {t('downloadWildmetaApp')}
            </Button>

            {/* APK Download Button */}
            {apkDownloadData && isAndroid && (
              <Button
                onClick={handleDownloadApk}
                w="full"
                bg="gray.900"
                color="white"
                borderRadius="full"
                h={12}
                fontWeight="medium"
                fontSize="sm"
                _hover={{ bg: 'gray.800', boxShadow: 'lg' }}
                _active={{ bg: 'gray.950' }}
                transition="all 0.2s"
                boxShadow="md"
              >
                {t('downloadApk')}
              </Button>
            )}

            {/* Invite Code Section */}
            {inviteCode && (
              <VStack gap={2} w="full" align="start">
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.600"
                  textAlign="center"
                >
                  {t('inviteCode')}
                </Text>
                <HStack
                  w="full"
                  h={51}
                  bg="rgba(23, 155, 144, 0.15)"
                  rounded={8}
                  pl={'14px'}
                  border="1px dashed"
                  borderColor="#179B90"
                  justify="space-between"
                  align="center"
                >
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    color="#232527"
                    letterSpacing="widest"
                  >
                    {inviteCode}
                  </Text>
                  <IconButton
                    aria-label="Copy invite code"
                    size="sm"
                    variant="ghost"
                    colorScheme="gray"
                    onClick={handleCopyInviteCode}
                    _hover={{ bg: 'gray.100' }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="19"
                      viewBox="0 0 18 19"
                      fill="none"
                    >
                      <path
                        d="M5.24985 4.99609V2.74609C5.24985 2.33188 5.58564 1.99609 5.99985 1.99609H14.9998C15.4141 1.99609 15.7498 2.33188 15.7498 2.74609V13.2461C15.7498 13.6603 15.4141 13.9961 14.9998 13.9961H12.7498V16.2454C12.7498 16.66 12.4124 16.9961 11.9948 16.9961H3.00499C2.58794 16.9961 2.25 16.6626 2.25 16.2454L2.25195 5.74675C2.25202 5.33218 2.58948 4.99609 3.00706 4.99609H5.24985ZM3.75181 6.49609L3.75014 15.4961H11.2498V6.49609H3.75181ZM6.74985 4.99609H12.7498V12.4961H14.2498V3.49609H6.74985V4.99609Z"
                        fill="black"
                      />
                    </svg>
                  </IconButton>
                </HStack>
              </VStack>
            )}
          </VStack>

          {/* Footer Section */}
          <VStack gap={6} align="center" mb={9}>
            {/* Hyperliquid Logo Text */}
            <HStack
              gap={2}
              align="center"
              justify="space-between"
              px={6}
              py={3}
              bg={'#F0F0F0'}
              rounded={12}
              onClick={() => {
                window.open('https://wildmeta.ai/');
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="21"
                height="20"
                viewBox="0 0 21 20"
                fill="none"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M10.5003 16.668C14.1822 16.668 17.167 13.6832 17.167 10.0013C17.167 6.3194 14.1822 3.33464 10.5003 3.33464C6.81843 3.33464 3.83366 6.3194 3.83366 10.0013C3.83366 13.6832 6.81843 16.668 10.5003 16.668ZM10.5003 18.3346C15.1027 18.3346 18.8337 14.6037 18.8337 10.0013C18.8337 5.39893 15.1027 1.66797 10.5003 1.66797C5.89795 1.66797 2.16699 5.39893 2.16699 10.0013C2.16699 14.6037 5.89795 18.3346 10.5003 18.3346Z"
                  fill="black"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M11.9552 15.1485C12.5708 13.9173 12.9997 12.0999 12.9997 10.0013C12.9997 7.90275 12.5708 6.08527 11.9552 4.8541C11.2898 3.52324 10.6789 3.33464 10.4997 3.33464C10.3205 3.33464 9.70954 3.52324 9.04411 4.8541C8.42852 6.08527 7.99967 7.90275 7.99967 10.0013C7.99967 12.0999 8.42852 13.9173 9.04411 15.1485C9.70954 16.4794 10.3205 16.668 10.4997 16.668C10.6789 16.668 11.2898 16.4794 11.9552 15.1485ZM10.4997 18.3346C12.8009 18.3346 14.6663 14.6037 14.6663 10.0013C14.6663 5.39893 12.8009 1.66797 10.4997 1.66797C8.19849 1.66797 6.33301 5.39893 6.33301 10.0013C6.33301 14.6037 8.19849 18.3346 10.4997 18.3346Z"
                  fill="black"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M18.7925 10.8346C18.8197 10.5605 18.8337 10.2826 18.8337 10.0013C18.8337 9.72005 18.8197 9.44206 18.7925 9.16797H2.20814C2.18093 9.44206 2.16699 9.72005 2.16699 10.0013C2.16699 10.2826 2.18093 10.5605 2.20814 10.8346H18.7925Z"
                  fill="black"
                />
              </svg>
              <Text fontSize="14px" color="#000" fontWeight={500}>
                {t('officialWebsite')}
              </Text>
              <HStack>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="21"
                  height="20"
                  viewBox="0 0 21 20"
                  fill="none"
                >
                  <path
                    d="M13.8362 7.845L6.66368 15.0175L5.48535 13.8392L12.657 6.66667H6.33619V5H15.5029V14.1667H13.8362V7.845Z"
                    fill="black"
                  />
                </svg>
              </HStack>
            </HStack>

            {/* Additional Footer Text */}
            <HStack gap={1.5} align="center">
              <Text fontSize="14px" color="#072723" fontWeight={700}>
                {t('tradeBetterOn')}
              </Text>
              <HLLogo />
            </HStack>
          </VStack>
        </VStack>
      </Container>
    </Center>
  );
}
